/**
 * The permission engine. Pure functions shared by server (enforcement) and
 * client (hiding UI). The server is always the authority.
 *
 * Role references: `discord:<role id>` or `local:<role id>`.
 * Policy nodes:    `dashboard`, `section:<id>`, `tool:<id>`, `feature:<key>`.
 */

export type RoleRef = string;

export interface Policy {
  allowAny: RoleRef[];
  requireAll: RoleRef[];
  deny: RoleRef[];
}

export type Verdict = 'allow' | 'deny' | 'inherit';
export type ToolStatus = 'beta' | 'live' | 'disabled';
export type FeatureDefault = 'inherit' | 'admin';

export const ROLE_ADMIN = 'local:platform-admin';
export const ROLE_AUDITOR = 'local:auditor';
export const ROLE_TESTER = 'local:tester';
/** Built-in local roles that only take effect after the holder elevates, every session. This can't be turned off. */
export const ALWAYS_ELEVATE = ['platform-admin', 'auditor', 'tester'];

export const emptyPolicy = (): Policy => ({ allowAny: [], requireAll: [], deny: [] });

export function isEmptyPolicy(p?: Policy | null): boolean {
  return !p || (p.allowAny.length === 0 && p.requireAll.length === 0 && p.deny.length === 0);
}

/** Deny wins; an empty allow/require pair means "inherit from the level above". */
export function evaluatePolicy(p: Policy | undefined | null, roles: Set<RoleRef>): Verdict {
  if (!p) return 'inherit';
  if (p.deny.some((r) => roles.has(r))) return 'deny';
  if (p.allowAny.length === 0 && p.requireAll.length === 0) return 'inherit';
  const anyOk = p.allowAny.length === 0 || p.allowAny.some((r) => roles.has(r));
  const allOk = p.requireAll.every((r) => roles.has(r));
  return anyOk && allOk ? 'allow' : 'deny';
}

export interface FeatureDef {
  key: string;
  label: string;
  default?: FeatureDefault;
  /** Until a policy is set: also these Discord roles, matched by name (e.g. "Ravenbound Management"). */
  defaultRoles?: string[];
}

export interface ToolAccessDef {
  id: string;
  section: string;
  status: ToolStatus;
  features: FeatureDef[];
  /** Closed to non-admins until a tool clearance policy allows a role (e.g. Analytics). */
  restricted?: boolean;
}

export interface AccessModel {
  policies: Record<string, Policy>;
  tools: ToolAccessDef[];
  sections: string[];
}

export interface AccessResult {
  isAdmin: boolean;
  dashboard: boolean;
  sections: Record<string, boolean>;
  tools: Record<string, boolean>;
  features: Record<string, boolean>;
}

/** Admin-panel permissions, modelled as features of a pseudo-tool `admin` (default: admins only). */
export const ADMIN_FEATURES: FeatureDef[] = [
  { key: 'admin.users.view', label: 'View users' },
  { key: 'admin.users.manage', label: 'Manage users (local roles, status)' },
  { key: 'admin.roles.view', label: 'View roles' },
  { key: 'admin.roles.manage', label: 'Manage local roles' },
  { key: 'admin.clearance.view', label: 'View clearance controls' },
  { key: 'admin.clearance.manage', label: 'Edit clearance controls' },
  { key: 'admin.discord.view', label: 'View Discord connection' },
  { key: 'admin.discord.manage', label: 'Manage Discord settings and sync' },
  { key: 'admin.backups.view', label: 'View backups' },
  { key: 'admin.backups.manage', label: 'Create and download backups' },
  { key: 'admin.logs.view', label: 'View system log' },
  { key: 'admin.revisions.view', label: 'View revisions' },
  { key: 'admin.revisions.manage', label: 'Add revision entries' },
  { key: 'admin.datasources.view', label: 'View data sources' },
  { key: 'admin.datasources.manage', label: 'Check and refresh data sources' },
  { key: 'admin.settings.manage', label: 'Manage platform settings' },
  { key: 'admin.pages.code', label: 'Add HTML and iframe modules to pages (code that runs in members’ browsers, sandboxed)' },
].map((f) => ({ ...f, default: 'admin' as const }));

export function resolveAccess(model: AccessModel, roles: Set<RoleRef>): AccessResult {
  const isAdmin = roles.has(ROLE_ADMIN);
  const pol = (k: string) => model.policies[k];

  // Admins always pass the dashboard gate unless one of their roles is on its deny list,
  // so a mis-set allow list can never lock every admin (or the break-glass account) out.
  const dp = pol('dashboard');
  const dashboard = isAdmin ? !(dp?.deny ?? []).some((r) => roles.has(r)) : evaluatePolicy(dp, roles) !== 'deny';

  const sections: Record<string, boolean> = {};
  for (const s of model.sections) {
    sections[s] = dashboard && (isAdmin || evaluatePolicy(pol(`section:${s}`), roles) !== 'deny');
  }

  const tools: Record<string, boolean> = {};
  const features: Record<string, boolean> = {};
  for (const t of model.tools) {
    let ok = t.status !== 'disabled' && !!sections[t.section];
    if (ok && !isAdmin) {
      if (t.status === 'beta' && !roles.has(ROLE_TESTER)) ok = false;
      else {
        const v = evaluatePolicy(pol(`tool:${t.id}`), roles);
        ok = t.restricted ? v === 'allow' : v !== 'deny';
      }
    }
    tools[t.id] = ok;
    for (const f of t.features) {
      if (!ok) { features[f.key] = false; continue; }
      if (isAdmin) { features[f.key] = true; continue; }
      const v = evaluatePolicy(pol(`feature:${f.key}`), roles);
      features[f.key] = v === 'inherit' ? f.default !== 'admin' : v === 'allow';
    }
  }

  for (const f of ADMIN_FEATURES) {
    features[f.key] = dashboard && (isAdmin || evaluatePolicy(pol(`feature:${f.key}`), roles) === 'allow');
  }

  return { isAdmin, dashboard, sections, tools, features };
}

/** Admin features that don't open the Admin Panel by themselves. */
const NOT_PANEL = new Set(['admin.pages.code']);
export const hasAdminAccess = (a: AccessResult) =>
  a.isAdmin || ADMIN_FEATURES.some((f) => !NOT_PANEL.has(f.key) && a.features[f.key]);

/** Replace role references across a set of policies (used by the role remap). */
export function remapPolicy(p: Policy, map: Record<RoleRef, RoleRef>): Policy {
  const m = (list: RoleRef[]) => Array.from(new Set(list.map((r) => map[r] ?? r)));
  return { allowAny: m(p.allowAny), requireAll: m(p.requireAll), deny: m(p.deny) };
}
